import { Execute } from './formula.interpretation';
import { Check, IsNt, IsT, SELECT } from './formula.parsing';
import type { Descriptor, ErrorCorrection, Terms, TweObj } from './formula.types';
import { type Token, TokenTypes } from './formula.types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function Correct(input: string, context: Record<string, any> = {}): string {
  function correctMulti(input: string, errors: ErrorCorrection[], exampleId = 'ID'): string {
    function correctSingle(input: string, error: ErrorCorrection) {
      if ('deleteFrom' in error) {
        return input.slice(0, error.deleteFrom) + input.slice(error.deleteTo);
      } else if ('insert' in error) {
        return (
          input.slice(0, error.position) +
          error.insert.split('ID').join(exampleId) +
          input.slice(error.position)
        );
      } else {
        return (
          input.slice(0, error.replaceFrom) +
          error.replaceWith.split('ID').join(exampleId) +
          input.slice(error.replaceTo)
        );
      }
    }

    errors = errors.sort((e) =>
      'deleteFrom' in e ? e.deleteFrom : 'replaceFrom' in e ? e.replaceFrom : e.position,
    );
    for (const error of errors) {
      input = correctSingle(input, error);
    }

    input = input.replace('{_', '{');
    return input;
  }

  let iterations = 0;
  const contextKeys = Object.keys(context);
  for (
    let result = Execute(input, context);
    input.length > 0 && ++iterations < 6 && result.success !== true;
    result = Execute(input, context)
  ) {
    input = correctMulti(
      input,
      result.errors.map((e) => e.correction),
      contextKeys.length > 0 ? contextKeys[0] : 'ID',
    );
  }

  return input;
}

/**
 * Recovers from an error.
 *
 * @remarks
 * See {@link https://vs.inf.ethz.ch/publ/papers/Error-Recovery-LL1.pdf | this paper}.
 */
export function Error(
  current: Descriptor,
  input: string,
  tweSet: Record<number, TweObj[]>,
): Descriptor {
  const { position, production, parent, errors } = current;

  // Typo correction.
  if (production && production.length > 0) {
    const firstProduction = production[0];
    if (firstProduction) {
      const selectSet = IsNt(firstProduction) ? SELECT[firstProduction.nt] : [firstProduction.t];
      for (const x of selectSet ?? []) {
        const similar = Similar(position, x, tweSet);
        if (similar === null) continue;

        if (tweSet[position]) {
          tweSet[position].push(similar);
        }
        return {
          ...current,
          errors: [
            ...current.errors,
            {
              message: `Typo: ${similar.lexeme} changed to ${similar.token}`,
              correction: {
                replaceFrom: similar.start,
                replaceTo: similar.end,
                replaceWith: similar.lexeme,
              },
            },
          ],
        };
      }
    }
  }

  // Insertion
  const insertStack: ({ nt: string } | { t: Token })[][] = [production];
  const errorStack: { message: string; correction: ErrorCorrection }[] = [...errors];
  for (
    let recursiveParent = parent;
    recursiveParent !== null && InsertCost(insertStack) < Reliability(position, tweSet);
    recursiveParent = recursiveParent.parent
  ) {
    errorStack.push(...recursiveParent.errors);
    const recursiveFirstProduction = recursiveParent.production[0];
    if (
      recursiveParent.production.length > 0 &&
      recursiveFirstProduction &&
      ((IsT(recursiveFirstProduction) && Check(position, recursiveFirstProduction)) ||
        (IsNt(recursiveFirstProduction) &&
          SELECT[recursiveFirstProduction.nt]?.some((t) => Check(position, { t }))))
    ) {
      const sententialFormToInsert = insertStack
        .flatMap((g) =>
          g
            .map((i) => (IsNt(i) ? i.nt : i.t.toString()))
            .filter((i) => i in EXAMPLES)
            .map((i) => EXAMPLES[i]),
        )
        .join(' ');

      return {
        ...recursiveParent,
        errors: [
          ...errorStack,
          {
            message: `${sententialFormToInsert} inserted.`,
            correction: { insert: sententialFormToInsert, position },
          },
        ],
      };
    }
    insertStack.push(recursiveParent.production);
  }

  if (position >= input.trimEnd().length) {
    // Unexpected EOF. We'll need to insert the whole stack.
    const sententialFormToInsert = insertStack
      .flatMap((g) =>
        g
          .map((i) => (IsNt(i) ? i.nt : i.t.toString()))
          .filter((i) => i in EXAMPLES)
          .map((i) => EXAMPLES[i]),
      )
      .join(' ');

    return {
      ...current,
      production: [],
      parent: null,
      errors: [
        ...errorStack,
        {
          message: `${sententialFormToInsert} inserted.`,
          correction: { insert: sententialFormToInsert, position },
        },
      ],
    };
  } else if (!(position in tweSet)) {
    // Uh-oh! We desynced! Find the next available spot and keep going.
    const nextPosition = Math.min(
      ...Object.keys(tweSet)
        .map(parseInt)
        .filter((k) => k >= position),
    );

    if (nextPosition in tweSet)
      return Error(
        {
          ...current,
          position: nextPosition,
          errors: [
            ...errors,
            {
              message: `${input.slice(position, nextPosition)} deleted.`,
              correction: { deleteFrom: position, deleteTo: nextPosition },
            },
          ],
        },
        input,
        tweSet,
      );
    // We're really done for now! Not much to do but give up.
    else
      return {
        parent: null,
        production: [],
        errors: [
          {
            message: `Unable to parse input ${input}.`,
            correction: { deleteFrom: 0, deleteTo: input.length },
          },
        ],
        process: () =>
          ({
            extents: { start: 0, end: 0 },
            values: [],
          }) as Terms.Group,
        position: input.length,
        subtrees: [],
      };
  }

  // Insertion & typo correction failed. Delete and try again!
  const minimalDeletionCandidate = tweSet[position]?.reduce((prev, cur) =>
    cur.end < prev.end ? cur : prev,
  );

  if (!minimalDeletionCandidate) {
    return {
      parent: null,
      production: [],
      errors: [
        {
          message: `Unable to parse input ${input}.`,
          correction: { deleteFrom: 0, deleteTo: input.length },
        },
      ],
      process: () =>
        ({
          extents: { start: 0, end: 0 },
          values: [],
        }) as Terms.Group,
      position: input.length,
      subtrees: [],
    };
  }
  return Error(
    {
      ...current,
      errors: [
        ...errors,
        {
          message: `${minimalDeletionCandidate.lexeme} deleted.`,
          correction: { deleteFrom: position, deleteTo: minimalDeletionCandidate.end },
        },
      ],
      position: minimalDeletionCandidate.end,
    },
    input,
    tweSet,
  );
}

/** Finds the most similar option to a given token at a given position if a sufficiently similar one exists. */
function Similar(position: number, x: Token, tweSet: Record<number, TweObj[]>): TweObj | null {
  if (!(position in tweSet)) return null;

  for (const candidate of tweSet[position] as TweObj[]) {
    // Check for parenthesis swapping/mis-shifting
    if (
      (x === TokenTypes.OPEN_PARENS &&
        (candidate.token === TokenTypes.CLOSE_PARENS || candidate.lexeme.trim() === '9')) ||
      (x === TokenTypes.CLOSE_PARENS &&
        (candidate.token === TokenTypes.OPEN_PARENS || candidate.lexeme.trim() === '0'))
    )
      return candidate;

    // TODO: More common typoes.
  }

  return null;
}

/** Determines the cost of inserting a given sentential form. */
function InsertCost(stack: ({ nt: string } | { t: Token })[][]): number {
  let totalCost = 0;
  for (const substack of stack) {
    for (const item of substack) {
      if (IsNt(item)) {
        // Tails can always derive ε, so no need to think about inserting them.
        if (item.nt.endsWith('Tail')) continue;
        if (item.nt.endsWith('Expr')) {
          totalCost += 1; // Single ID.
          continue;
        }

        switch (item.nt) {
          case 'Expression':
          case 'Value':
            totalCost += 1; // Single ID
            continue;
          case 'Application':
            totalCost += 3; // ID, open parens, close parens
            continue;
          case 'ArgList':
            continue; // ε
        }
      } else {
        totalCost += 1; // No keywords, everything else is short.
      }
    }
  }

  return totalCost;
}

/** Determines the maximum reliability of any token possibly attainable starting from the given position. */
function Reliability(position: number, tweSet: Record<number, TweObj[]>): number {
  if (!(position in tweSet)) return 100000;

  let maxReliability = 0;
  for (const option of tweSet[position] as TweObj[]) {
    switch (option.token) {
      case TokenTypes.ID:
        maxReliability = Math.max(maxReliability, 1); // Identifiers have a reliability of 1.
        continue;

      case TokenTypes.CLOSE_PARENS:
        return 20; // Close parens are a stop token and thus have very high reliability to correct bad assumptions.

      default:
        maxReliability = Math.max(maxReliability, 1); // All other symbols have a reliability of 3.
        continue;
    }
  }

  return maxReliability;
}

const EXAMPLES: Record<string, string> = {
  Expression: 'ID',
  CompExpr: 'ID',
  AddSubExpr: 'ID',
  MulDivExpr: 'ID',
  Value: 'ID',
  Application: 'ID()',
  ID: 'ID',
  OPEN_PARENS: '(',
  CLOSE_PARENS: ')',
  EQUALS: '=',
  COMMA: ',',
  COMPOP: '>',
  MULDIVOP: '*',
  ADDSUBOP: '+',
};
